import React, { useEffect } from 'react';
import {
  ArrowRight,
  BadgeCheck,
  Boxes,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Factory,
  FileCheck2,
  Gauge,
  Globe2,
  Landmark,
  LayoutList,
  MapPinPlus,
  PackageCheck,
  Scale,
  ScanSearch,
  Settings2,
  ShieldCheck,
  ShoppingBag,
  Store,
  Tags,
  UserRoundCheck,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CONSUMER_AFFAIRS_LOGO = '/legacy-source/assets/services/consumeraffair.png';
const PACKAGING_IMAGE = '/legacy-source/assets/top-services/legal-metrology-packaging.webp';
const PROCESS_IMAGE = '/legacy-source/assets/subservices/process/lmpc-process.webp';

const serviceModules = [
  {
    icon: LayoutList,
    number: '01',
    title: 'Retail package declaration compliance',
    text: 'Identify the declarations applicable to each packaged product, segment and route to market.',
    points: ['Product-wise applicability assessment', 'Drafting and validation of declarations', 'Packaging information review', 'Standard formats and templates'],
    accent: 'rose',
  },
  {
    icon: FileCheck2,
    number: '02',
    title: 'Legal Metrology registration',
    text: 'End-to-end assistance for manufacturer, packer and importer registration requirements.',
    points: ['Documentation preparation', 'Application filing', 'Authority coordination', 'Follow-up through completion'],
    accent: 'sky',
  },
  {
    icon: Tags,
    number: '03',
    title: 'Packaging and labelling compliance',
    text: 'Review artwork and package declarations before products are released into the Indian market.',
    points: ['MRP and net quantity', 'Entity name and address', 'Country of origin', 'Consumer-care information'],
    accent: 'amber',
  },
  {
    icon: ScanSearch,
    number: '04',
    title: 'Legal Metrology label audit',
    text: 'Find non-conformities early and translate the findings into practical artwork corrections.',
    points: ['Detailed artwork review', 'Non-conformity report', 'Corrective recommendations', 'Revised-artwork review'],
    accent: 'emerald',
  },
  {
    icon: Settings2,
    number: '05',
    title: 'Compliance systems and tools',
    text: 'Build repeatable internal controls for new products, revised packs and declaration changes.',
    points: ['Label approval mechanism', 'Internal checklists', 'Artwork change control', 'Compliance tracking system'],
    accent: 'violet',
  },
  {
    icon: Boxes,
    number: '06',
    title: 'Pack classification',
    text: 'Determine whether the proposed pack and distribution model is retail, wholesale or industrial/institutional.',
    points: ['Usage and distribution mapping', 'Sales-channel assessment', 'Declaration applicability', 'Classification risk review'],
    accent: 'orange',
  },
  {
    icon: PackageCheck,
    number: '07',
    title: 'Mandatory retail declarations',
    text: 'Establish a complete declaration framework for every applicable product category.',
    points: ['Country of origin', 'Manufacture or import month/year', 'Generic commodity name', 'Consumer declarations'],
    accent: 'rose',
  },
  {
    icon: MapPinPlus,
    number: '08',
    title: 'Renewal and business-place additions',
    text: 'Manage registration continuity when an organisation adds or changes operational locations.',
    points: ['Renewal support', 'New place of business', 'Registration amendments', 'Supporting documentation'],
    accent: 'sky',
  },
  {
    icon: UserRoundCheck,
    number: '09',
    title: 'Director nomination — Section 49',
    text: 'Support the assessment, preparation and filing of applicable company nomination records.',
    points: ['Applicability assessment', 'Nomination documentation', 'Declarations and filing', 'Authority follow-up'],
    accent: 'amber',
  },
  {
    icon: BadgeCheck,
    number: '10',
    title: 'Registration under Rule 27',
    text: 'Prepare and coordinate applicable manufacturer, packer or importer registration filings.',
    points: ['Applicant-role mapping', 'Document verification', 'Application submission', 'Registration follow-up'],
    accent: 'emerald',
  },
];

const declarations = [
  ['MRP', 'Maximum Retail Price, inclusive of applicable taxes'],
  ['Net quantity', 'Weight, measure or number expressed in the prescribed manner'],
  ['Entity details', 'Manufacturer, packer or importer name and address'],
  ['Commodity name', 'Generic or common name of the packaged commodity'],
  ['Country of origin', 'Applicable disclosure for imported products'],
  ['Month and year', 'Manufacture, packing or import information, as applicable'],
  ['Consumer care', 'Contact details for consumer complaints and enquiries'],
  ['Category-specific', 'Other declarations applicable to the product and pack'],
];

const approach = [
  ['01', 'Compliance assessment', 'Understand the product, packaging, applicant role, business model and distribution channel.'],
  ['02', 'Documentation and registration', 'Prepare the applicable application, declarations and supporting records.'],
  ['03', 'Label and artwork review', 'Review every visible declaration and identify gaps before printing or sale.'],
  ['04', 'Corrective action', 'Translate review findings into clear amendments and implementation support.'],
  ['05', 'Regulatory coordination', 'Coordinate applications, amendments, registrations and statutory follow-up.'],
  ['06', 'Ongoing compliance support', 'Support new products, revised packs, location changes and periodic reviews.'],
];

const beneficiaries = [
  [Factory, 'Manufacturers'],
  [Globe2, 'Importers'],
  [Boxes, 'Packers'],
  [ShieldCheck, 'Brand owners'],
  [ShoppingBag, 'E-commerce businesses'],
  [Store, 'Retail businesses'],
  [BriefcaseBusiness, 'FMCG companies'],
  [Gauge, 'Electronics businesses'],
  [Building2, 'Industrial companies'],
  [PackageCheck, 'Packaged-goods sellers'],
];

const accentClasses = {
  rose: 'border-rose-200 bg-rose-50 text-rose-700',
  sky: 'border-sky-200 bg-sky-50 text-sky-700',
  amber: 'border-amber-200 bg-amber-50 text-amber-700',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  violet: 'border-violet-200 bg-violet-50 text-violet-700',
  orange: 'border-orange-200 bg-orange-50 text-orange-700',
};

const LegalMetrologyCompliancePage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.title = 'Legal Metrology Compliance Services | Complise India';
    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.content = 'End-to-end Legal Metrology support for packaged commodities, including registrations, Rule 27, label audits, retail declarations, amendments and compliance systems.';
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-neutral-900">
      <Header />
      <main>
        <section className="relative overflow-hidden border-b border-neutral-200 bg-gradient-to-br from-white via-rose-50/30 to-amber-50/50">
          <div className="absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-rose-100/65 blur-3xl" />
          <div className="absolute -bottom-44 left-[35%] h-[420px] w-[420px] rounded-full bg-amber-100/60 blur-3xl" />
          <div className="relative mx-auto grid max-w-[1280px] items-center gap-12 px-6 py-16 lg:grid-cols-[1.06fr_0.94fr] lg:px-10 lg:py-24">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white/90 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#C41E3A] shadow-sm">
                <Scale className="h-3.5 w-3.5" /> Consumer Affairs · Packaged Commodities
              </div>
              <h1 className="max-w-[760px] font-display text-4xl font-extrabold leading-[1.02] tracking-[-0.045em] text-neutral-950 md:text-6xl lg:text-[4.3rem]">
                Legal Metrology compliance services, from pack to market.
              </h1>
              <p className="mt-6 max-w-[720px] text-base leading-8 text-neutral-600 md:text-lg">
                End-to-end support for manufacturers, importers, packers and brands establishing compliant packaging, completing statutory registrations, reviewing labels and managing ongoing Legal Metrology requirements in India.
              </p>
              <div className="mt-7 flex flex-wrap gap-2">
                {['Rule 27 registration', 'Label audit', 'Retail declarations', 'Business amendments'].map((item) => (
                  <span key={item} className="rounded-full border border-neutral-200 bg-white px-3 py-2 text-[11px] font-bold text-neutral-700 shadow-sm">{item}</span>
                ))}
              </div>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <a href="/contact" className="group inline-flex h-[52px] items-center justify-center gap-3 rounded-full bg-[#C41E3A] px-6 text-sm font-bold text-white shadow-[0_16px_34px_-16px_rgba(196,30,58,0.8)] transition hover:-translate-y-0.5 hover:bg-[#a81830]">
                  Assess my packaged product <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
                <a href="#services" className="inline-flex h-[52px] items-center justify-center gap-2 rounded-full border border-neutral-300 bg-white px-6 text-sm font-bold text-neutral-800 transition hover:border-[#C41E3A] hover:text-[#C41E3A]">
                  Explore service scope <ChevronRight className="h-4 w-4" />
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-5 rounded-[2.5rem] bg-gradient-to-br from-rose-200/40 via-amber-100/50 to-sky-100/50 blur-2xl" />
              <div className="relative overflow-hidden rounded-[2rem] border border-neutral-200 bg-white p-4 shadow-[0_35px_90px_-42px_rgba(15,23,42,0.42)] md:p-5">
                <div className="relative h-[390px] overflow-hidden rounded-[1.55rem] md:h-[470px]">
                  <img src={PACKAGING_IMAGE} alt="Packaged consumer goods undergoing Legal Metrology label review" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/80 via-neutral-950/5 to-transparent" />
                  <div className="absolute left-5 top-5 grid h-20 w-20 place-items-center rounded-2xl bg-white p-2.5 shadow-xl">
                    <img src={CONSUMER_AFFAIRS_LOGO} alt="Department of Consumer Affairs emblem" className="h-full w-full object-contain" />
                  </div>
                  <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/15 bg-white/10 p-5 text-white backdrop-blur-md">
                    <span className="text-[9px] font-black uppercase tracking-[0.2em] text-amber-300">Package readiness view</span>
                    <h2 className="mt-2 font-display text-2xl font-extrabold">Declare. Review. Register. Control.</h2>
                    <div className="mt-4 grid grid-cols-4 gap-2">
                      {['MRP', 'Quantity', 'Origin', 'Care'].map((item) => <span key={item} className="rounded-lg bg-white/10 px-2 py-2 text-center text-[9px] font-bold">{item}</span>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-neutral-200 bg-white">
          <div className="mx-auto grid max-w-[1280px] grid-cols-2 gap-px bg-neutral-200 md:grid-cols-4">
            {[[Factory, 'Manufacturers'], [Globe2, 'Importers'], [Boxes, 'Packers'], [ShieldCheck, 'Brand owners']].map(([Icon, label]) => (
              <div key={label} className="flex items-center gap-3 bg-white px-5 py-5"><Icon className="h-5 w-5 text-[#C41E3A]" /><span className="text-xs font-bold text-neutral-700">{label}</span></div>
            ))}
          </div>
        </section>

        <section id="services" className="mx-auto max-w-[1280px] px-6 py-16 lg:px-10 lg:py-24">
          <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-8">
              <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#C41E3A]">Complete service scope</span>
              <h2 className="mt-3 max-w-4xl font-display text-3xl font-extrabold leading-tight md:text-5xl">Ten connected workstreams for packaged-commodity compliance.</h2>
            </div>
            <p className="text-sm leading-7 text-neutral-600 lg:col-span-4">Each engagement is adapted to the product, pack, applicant role, distribution model and jurisdiction involved.</p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {serviceModules.map(({ icon: Icon, number, title, text, points, accent }) => (
              <article key={number} className="group relative overflow-hidden rounded-[1.85rem] border border-neutral-200 bg-white p-6 transition duration-300 hover:-translate-y-1.5 hover:shadow-[0_28px_70px_-46px_rgba(196,30,58,0.65)] md:p-7">
                <span className="absolute -right-2 top-0 font-display text-[6rem] font-black leading-none text-neutral-50 transition group-hover:text-rose-50">{number}</span>
                <div className="relative flex items-start gap-4">
                  <span className={`grid h-[52px] w-[52px] shrink-0 place-items-center rounded-2xl border ${accentClasses[accent]}`}><Icon className="h-5 w-5" /></span>
                  <div><span className="text-[9px] font-black uppercase tracking-[0.2em] text-neutral-400">Service {number}</span><h3 className="mt-1 font-display text-xl font-extrabold leading-snug md:text-2xl">{title}</h3></div>
                </div>
                <p className="relative mt-5 text-sm leading-7 text-neutral-600">{text}</p>
                <div className="relative mt-5 grid gap-2 sm:grid-cols-2">
                  {points.map((point) => <span key={point} className="flex items-start gap-2 text-[11px] font-semibold leading-5 text-neutral-600"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />{point}</span>)}
                </div>
                {number === '04' && <p className="relative mt-5 rounded-xl bg-amber-50 px-4 py-3 text-[10px] leading-5 text-amber-900">Where an on-site visit is required, travel and stay arrangements are borne by the client/company.</p>}
              </article>
            ))}
          </div>
        </section>

        <section id="declarations" className="overflow-hidden bg-[#0b1724] py-16 text-white lg:py-24">
          <div className="mx-auto grid max-w-[1280px] gap-12 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:px-10">
            <div className="relative">
              <div className="absolute -inset-5 rounded-[2rem] bg-rose-500/10 blur-2xl" />
              <div className="relative overflow-hidden rounded-[2rem] border border-white/10">
                <img src={PROCESS_IMAGE} alt="Legal Metrology packaged commodity compliance process" className="h-[420px] w-full object-cover opacity-80 md:h-[520px]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b1724] via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-7"><span className="text-[10px] font-black uppercase tracking-[0.2em] text-rose-300">Before artwork release</span><h3 className="mt-2 font-display text-3xl font-extrabold">Review every declaration as one connected label system.</h3></div>
              </div>
            </div>
            <div>
              <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-rose-300">Mandatory declaration framework</span>
              <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight md:text-5xl">What a retail package review examines.</h2>
              <p className="mt-5 text-sm leading-7 text-white/60">The exact declarations depend on the commodity, pack and applicable rules. The review brings the core information into one controlled checklist.</p>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {declarations.map(([title, text], index) => (
                  <article key={title} className="rounded-2xl border border-white/10 bg-white/[0.05] p-4 transition hover:border-rose-300/40 hover:bg-white/[0.08]">
                    <div className="flex items-center gap-3"><span className="grid h-8 w-8 place-items-center rounded-lg bg-rose-400/15 text-[10px] font-black text-rose-300">0{index + 1}</span><strong className="text-sm">{title}</strong></div>
                    <p className="mt-3 text-[11px] leading-5 text-white/55">{text}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="classification" className="mx-auto max-w-[1280px] px-6 py-16 lg:px-10 lg:py-24">
          <div className="text-center">
            <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#C41E3A]">Classification before declaration</span>
            <h2 className="mx-auto mt-3 max-w-3xl font-display text-3xl font-extrabold leading-tight md:text-5xl">The pack and its intended buyer shape the compliance route.</h2>
          </div>
          <div className="mt-11 grid gap-5 lg:grid-cols-3">
            {[
              [ShoppingBag, 'Retail packages', 'Packages intended for sale to the ultimate consumer through retail or e-commerce channels.', 'Consumer-facing declarations'],
              [Store, 'Wholesale packages', 'Packages used in wholesale distribution require classification against their actual supply arrangement.', 'Distribution-led assessment'],
              [Factory, 'Industrial / institutional packs', 'Packages supplied for qualifying industrial or institutional use need evidence-led applicability review.', 'Use-case documentation'],
            ].map(([Icon, title, text, tag], index) => (
              <article key={title} className="group rounded-[2rem] border border-neutral-200 bg-white p-7 shadow-[0_20px_60px_-48px_rgba(15,23,42,0.45)] transition hover:-translate-y-1 hover:border-rose-200">
                <div className="flex items-center justify-between"><span className="grid h-14 w-14 place-items-center rounded-2xl bg-rose-50 text-[#C41E3A]"><Icon className="h-6 w-6" /></span><span className="font-display text-5xl font-black text-neutral-100">0{index + 1}</span></div>
                <h3 className="mt-8 font-display text-2xl font-extrabold">{title}</h3><p className="mt-4 text-sm leading-7 text-neutral-600">{text}</p><span className="mt-6 inline-flex rounded-full bg-neutral-100 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-neutral-600">{tag}</span>
              </article>
            ))}
          </div>
        </section>

        <section id="approach" className="bg-white py-16 lg:py-24">
          <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
            <div className="grid gap-10 lg:grid-cols-[0.68fr_1.32fr]">
              <div className="lg:sticky lg:top-28 lg:self-start">
                <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#C41E3A]">Our compliance approach</span>
                <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight md:text-5xl">A controlled process from assessment to continuity.</h2>
                <p className="mt-5 text-sm leading-7 text-neutral-600">Registration is one part of the lifecycle. Sustainable compliance also needs accurate labels, classification, change control and periodic review.</p>
              </div>
              <div className="space-y-3">
                {approach.map(([number, title, text]) => (
                  <article key={number} className="group grid gap-4 rounded-2xl border border-neutral-200 bg-[#FDFBF7] p-5 transition hover:border-rose-200 hover:bg-rose-50/40 sm:grid-cols-[62px_1fr_auto] sm:items-center">
                    <span className="font-display text-2xl font-black text-[#C41E3A]">{number}</span><div><h3 className="font-display text-lg font-extrabold">{title}</h3><p className="mt-1 text-xs leading-6 text-neutral-600">{text}</p></div><ArrowRight className="hidden h-4 w-4 text-neutral-300 transition group-hover:translate-x-1 group-hover:text-[#C41E3A] sm:block" />
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="who-we-help" className="mx-auto max-w-[1280px] px-6 py-16 lg:px-10 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-[0.92fr_1.08fr] lg:items-center">
            <div>
              <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#C41E3A]">Who can benefit</span>
              <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight md:text-5xl">Built for businesses putting packaged commodities into India.</h2>
              <p className="mt-5 text-sm leading-7 text-neutral-600">Support can be scoped for one product, a complete label portfolio, registration work or an ongoing packaging-control program.</p>
              <div className="mt-7 grid grid-cols-2 gap-3">
                {beneficiaries.map(([Icon, title]) => <div key={title} className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white p-3"><span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-rose-50 text-[#C41E3A]"><Icon className="h-4 w-4" /></span><span className="text-xs font-bold text-neutral-700">{title}</span></div>)}
              </div>
            </div>
            <div className="rounded-[2.25rem] bg-gradient-to-br from-[#171b2a] to-[#0b1724] p-7 text-white shadow-2xl md:p-10">
              <span className="grid h-14 w-14 place-items-center rounded-2xl bg-rose-400/15 text-rose-300"><ClipboardCheck className="h-6 w-6" /></span>
              <span className="mt-8 block text-[10px] font-black uppercase tracking-[0.2em] text-rose-300">Why Complise India</span>
              <h3 className="mt-3 font-display text-3xl font-extrabold">Compliance that continues after registration.</h3>
              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                {['Complete lifecycle support', 'Product and segment assessment', 'Practical label review', 'Regulatory documentation', 'Ongoing compliance advisory', 'Artwork and change control'].map((item) => <div key={item} className="flex gap-3 rounded-xl border border-white/10 bg-white/[0.05] p-3 text-xs font-semibold text-white/75"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-300" />{item}</div>)}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1280px] px-6 pb-16 lg:px-10 lg:pb-24">
          <div className="relative overflow-hidden rounded-[2.25rem] bg-gradient-to-br from-[#9f1730] via-[#c41e3a] to-[#ee4965] p-8 text-white shadow-[0_30px_80px_-38px_rgba(196,30,58,0.8)] md:p-12">
            <div className="absolute -right-20 -top-24 h-80 w-80 rounded-full border-[48px] border-white/[0.08]" />
            <div className="relative grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
              <div><span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/70">Build a compliant packaging framework</span><h2 className="mt-3 max-w-3xl font-display text-3xl font-extrabold leading-tight md:text-5xl">Share the product, pack artwork and business model.</h2><p className="mt-4 max-w-2xl text-sm leading-7 text-white/75">Our team will assess the likely registration, declaration, classification and label-review requirements for your route.</p></div>
              <a href="/contact" className="inline-flex h-[52px] items-center justify-center gap-3 rounded-full bg-white px-6 text-sm font-extrabold text-[#C41E3A] transition hover:-translate-y-1">Get Legal Metrology assessment <ArrowRight className="h-4 w-4" /></a>
            </div>
          </div>
          <div className="mt-5 flex flex-col justify-between gap-4 rounded-2xl border border-neutral-200 bg-white p-5 text-xs text-neutral-500 md:flex-row md:items-center">
            <span>Applicability and filing requirements depend on the product, packaging, applicant role, state jurisdiction and current legal requirements. Final requirements should be confirmed for the specific case.</span>
            <a href="https://consumeraffairs.nic.in/weightsmeasures/weight-and-measures" target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 items-center gap-2 font-bold text-[#C41E3A]">Department of Consumer Affairs <Landmark className="h-4 w-4" /></a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LegalMetrologyCompliancePage;
